import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CHANNEL_TYPES, type ChannelType, type LogEntryDTO } from '@sac/contracts'
import { api } from '../lib/api'
import { qk } from '../lib/query'
import { ChannelMark, channelName } from '../components/icons'

/**
 * Registro — o que respondemos, em série, para um admin conferir.
 *
 * Cada linha é um PAR pergunta/resposta e não uma conversa: "o que estamos respondendo" só se lê
 * ao lado do que foi perguntado, e uma conversa de quarenta mensagens vira uma linha só.
 *
 * A resposta aparece inteira. Cortar em três linhas transformaria a tela de conferência numa
 * tela de amostragem — dá para ver que houve resposta, não o que ela dizia.
 */

const PERIODOS: [string, string][] = [
  ['7', '7 dias'],
  ['30', '30 dias'],
  ['90', '90 dias'],
  ['', 'Tudo'],
]

const ORIGENS: Record<string, { label: string; cls: string; title: string }> = {
  automatica: { label: 'Automática', cls: 'auto', title: 'Enviada pela IA sozinha — ninguém leu antes' },
  ia: { label: 'IA', cls: 'ia', title: 'Sugestão da IA enviada sem uma letra mudada' },
  humana: { label: 'Escrita aqui', cls: 'hum', title: 'Escrita, ou editada, por alguém no SAC' },
  importada: { label: 'Fora do SAC', cls: 'imp', title: 'Escrita no app do canal e trazida pelo poller' },
}

/**
 * `channelName` dá o mesmo rótulo para a pré e a pós-venda do mesmo marketplace — correto na
 * bolha, onde o ícone e o contexto desambiguam, e inútil num select: "Mercado Livre" aparecia
 * duas vezes sem dizer qual era qual, e isolar as perguntas é metade do motivo desta tela.
 */
const CANAL_LABEL: Partial<Record<ChannelType, string>> = {
  mercadolivre: 'Mercado Livre (pós-venda)',
  mercadolivre_question: 'Mercado Livre (perguntas)',
  tiktok: 'TikTok (atendimento)',
  tiktok_comment: 'TikTok (comentários)',
}

const LIMIT = 25

function quando(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

/** Minutos em algo que se lê sem converter de cabeça. */
function espera(min: number | null) {
  if (min === null) return null
  if (min < 60) return `${min} min`
  const h = min / 60
  if (h < 48) return `${h.toFixed(1).replace('.', ',')} h`
  return `${Math.round(h / 24)} dias`
}

function Linha({ r, onOpen }: { r: LogEntryDTO; onOpen: () => void }) {
  const o = ORIGENS[r.origin]!
  const t = espera(r.minutesToAnswer)
  return (
    <div className="log-row">
      <div className="log-meta">
        <ChannelMark channel={r.channelType} size={15} />
        <b>{r.customerName || '—'}</b>
        <span className="t2">{r.storeName}</span>
        <span className="t2">{quando(r.createdAt)}</span>
        <span className={`log-origin ${o.cls}`} title={o.title}>
          {o.label}
          {r.origin === 'automatica' && r.certainty !== null ? ` · ${r.certainty}` : ''}
        </span>
        {/* Sem autor não vira "?" nem some: é um fato sobre a resposta, não um dado faltando.
            E a automática não é "sem autor" — o autor é a própria IA; o que falta é a leitura. */}
        <span className="t2">
          {r.authorName ?? (r.origin === 'automatica' ? 'sem revisão humana' : 'sem autor no SAC')}
        </span>
        {r.status === 'failed' && <span className="pill danger">falhou</span>}
        <span className="log-sp" />
        <button className="btn sec sm" onClick={onOpen}>Abrir conversa</button>
      </div>

      {r.question ? (
        <div className="log-q">
          <span className="log-qt">{r.question}</span>
          {t && <span className="t2 log-wait">respondido em {t}</span>}
        </div>
      ) : (
        <div className="log-q none">Nós puxamos assunto — não houve pergunta antes.</div>
      )}

      <div className="log-a">{r.answer || <span className="t2">(sem texto)</span>}</div>
    </div>
  )
}

export function Registro({ onOpenConversation }: { onOpenConversation?: (id: string) => void }) {
  const [q, setQ] = useState('')
  const [days, setDays] = useState('30')
  const [channel, setChannel] = useState('')
  const [author, setAuthor] = useState('')
  const [origin, setOrigin] = useState('')
  const [page, setPage] = useState(0)

  // Todo filtro volta para a primeira página: manter o offset com outro recorte mostra uma
  // página vazia e parece que não há nada.
  const troca = (set: (v: string) => void) => (v: string) => { set(v); setPage(0) }

  const { data: agents = [] } = useQuery({ queryKey: qk.agents(), queryFn: api.agents })

  const params = { q, days, channel, author, origin, limit: LIMIT, offset: page * LIMIT }
  const { data, isLoading } = useQuery({
    queryKey: qk.log(params),
    queryFn: () => api.log(params),
    placeholderData: (prev) => prev,
  })

  const rows = data?.rows ?? []
  const total = data?.total ?? 0

  return (
    <div className="crm">
      <div className="crm-head">
        <h2>Registro</h2>
        <p>
          Toda resposta que saiu daqui para um cliente, com a pergunta que a motivou ao lado.
          Inclui as perguntas do Mercado Livre e também o que foi respondido <b>no app do canal</b>,
          fora do SAC — hoje a maior parte. Notas internas não entram: não são resposta a ninguém.
        </p>
      </div>

      <div className="crm-filters">
        <input
          placeholder="Buscar na pergunta, na resposta ou no cliente…"
          value={q}
          onChange={(e) => troca(setQ)(e.target.value)}
        />
        <select value={days} onChange={(e) => troca(setDays)(e.target.value)}>
          {PERIODOS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
        </select>
        <select value={channel} onChange={(e) => troca(setChannel)(e.target.value)}>
          <option value="">Todos os canais</option>
          {CHANNEL_TYPES.map((c) => (
            <option key={c} value={c}>{CANAL_LABEL[c] ?? channelName(c)}</option>
          ))}
        </select>
        <select value={author} onChange={(e) => troca(setAuthor)(e.target.value)}>
          <option value="">Qualquer autor</option>
          <option value="fora">Sem autor (fora do SAC)</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={origin} onChange={(e) => troca(setOrigin)(e.target.value)}>
          <option value="">Qualquer origem</option>
          <option value="automatica">Automática (ninguém leu)</option>
          <option value="humana">Escrita aqui</option>
          <option value="ia">IA sem edição</option>
          <option value="importada">Fora do SAC</option>
        </select>
      </div>

      {isLoading ? (
        <div className="empty">Carregando…</div>
      ) : !rows.length ? (
        <div className="empty" style={{ padding: 28 }}>Nenhuma resposta com esses filtros.</div>
      ) : (
        <div className="log-list">
          {rows.map((r) => (
            <Linha key={r.messageId} r={r} onOpen={() => onOpenConversation?.(r.conversationId)} />
          ))}
        </div>
      )}

      <div className="ord-pager">
        <button className="btn sec sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
          Anterior
        </button>
        <span className="cfg-note">
          {rows.length ? `${page * LIMIT + 1}–${page * LIMIT + rows.length}` : '0'} de {total}
        </span>
        <button
          className="btn sec sm"
          disabled={(page + 1) * LIMIT >= total}
          onClick={() => setPage((p) => p + 1)}
        >
          Próxima
        </button>
      </div>
    </div>
  )
}
